import React, { useContext } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { constructionFeeInitial } from 'app/edozvola/cases/construction-fee/model/construction-fee.model';

import { useParams } from 'react-router-dom';
import { CaseTabContext } from 'app/edozvola/cases/case-form-wrapper';
import { fetchConstructionFeeQuery, saveConstructionFeeQuery } from 'app/edozvola/cases/construction-fee/service/construction-fee.queries';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { Col, Row } from 'reactstrap';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { Button, Input } from 'edozvola-frontend-lib/dist/ui/components';
import { useDispatch } from 'react-redux';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import {
  fetchConstructionFeeAmountQuery,
  fetchEstimatedConstructionCostQuery,
  fetchSpecialFeeQuery
} from 'app/edozvola/codebooks/codebooks.queries';
import { constructionFeeValidationSchema } from 'app/edozvola/cases/construction-fee/model/construction-fee.schema';
import {PaginatedDropdown} from "edozvola-frontend-lib/dist/ui/components/paginated-dropdown";
import { getCaseContext } from 'app/shared/util/context-util';
import {NumericFormat} from "react-number-format";

const ConstructionFee = () => {
  const { id: uuid } = useParams<{ id: string }>();
  const { sidebarItemPair } = getCaseContext();
  const dispatch = useDispatch();

  const { data: constructionFeeAmountOptions, isLoading: constructionFeeAmountOptionsLoading } = fetchConstructionFeeAmountQuery();
  const { data: specialFeeOptions, isLoading: specialFeeOptionsLoading } = fetchSpecialFeeQuery();
  const { data: estimatedConstructionCostOptions, isLoading: estimatedConstructionCostLoading } = fetchEstimatedConstructionCostQuery();

  const { data: constructionFeeData, isLoading: constructionFeeDataLoading } = fetchConstructionFeeQuery(uuid);

  const formik = useFormik({
    initialValues: constructionFeeData || constructionFeeInitial,
    validationSchema: constructionFeeValidationSchema,
    onSubmit: values => saveConstructionFee(values),
    validateOnMount: true,
    enableReinitialize: true,
  });

  const { mutate: saveConstructionFee, isPending: savingConstructionFee } = saveConstructionFeeQuery(uuid, formik.resetForm);

  const onConstructionFeeAmountChange = (option: any[]) => {
    formik.setFieldValue('calculatedConstructionFeeAmount', null);
    formik.setFieldValue('constructionFeeAmount', option);
  }

  const onCalculatedConstructionFeeAmountChange = (value: number) => {
    formik.setFieldValue('constructionFeeAmount', []);
    formik.setFieldValue('calculatedConstructionFeeAmount', value);
  }

  return (
    <>
      <FormikProvider value={formik}>
        <FormikValueObserver
          onChange={dirty => {
            if (dirty.dirty) {
              dispatch(setFormChanged(true));
            } else {
              dispatch(initCaseFormChange());
            }
          }}
        />
        <div className="form-header">Građevinska upravna pristojba</div>
        <div className={'d-flex justify-content-between flex-column gap-3'}>
          <Row>
            <Col md={6}>
              <Label labelText={'Broj računa - IBAN'} />
              <Input
                disabled
                value={formik.values.iban}
                id="iban"
                name="iban"
                onBlur={formik.handleBlur}
                error={!sidebarItemPair?.readOnly && formik.errors.iban ? `${formik.errors.iban}` : ''}
              />
            </Col>
            <Col md={6}>
              <Label labelText={'Model'} />
              <Input
                disabled
                value={formik.values.model}
                id="model"
                name="model"
                onBlur={formik.handleBlur}
                error={!sidebarItemPair?.readOnly && formik.errors.model ? `${formik.errors.model}` : ''}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Label labelText={'Poziv na broj'} />
              <Input
                value={formik.values.referralNumber}
                error={!sidebarItemPair?.readOnly && ((formik.errors.referralNumber as string) ?? '')}
                disabled
              />
            </Col>
            <Col md={6}>
              <Label labelText={'Iznos predefinirane pristojbe (€)'} />
              <PaginatedDropdown
                placeholder={!sidebarItemPair?.readOnly && 'Odaberi iznos pristojbe (€)'}
                onItemSelect={selected => formik.setFieldValue('constructionFeeAmount', selected)}
                selected={formik.values.constructionFeeAmount}
                labelKey={'value'}
                options={constructionFeeAmountOptionsLoading ? [] : constructionFeeAmountOptions}
                disabled={sidebarItemPair?.readOnly || formik.values.calculatedConstructionFeeAmount != null}
                error={!sidebarItemPair?.readOnly && (formik.errors.constructionFeeAmount as string)}
                showErrorMessage
                clearButton
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Label labelText={'Iznos izračunate pristojbe (€)'} />
              <div className="d-flex align-items-center border rounded">
                <NumericFormat
                  decimalScale={2}
                  allowNegative={false}
                  thousandSeparator="."
                  decimalSeparator=","
                  customInput={Input}
                  value={formik.values.calculatedConstructionFeeAmount ?? ''}
                  onValueChange={values => onCalculatedConstructionFeeAmountChange(values.floatValue)}
                  disabled={sidebarItemPair?.readOnly || formik.values.constructionFeeAmount?.length > 0}
                  className={
                    !sidebarItemPair?.readOnly && formik.errors.calculatedConstructionFeeAmount
                      ? 'is-invalid'
                      : 'flex-grow-1 border-0 shadow-none'
                  }
                />
              </div>
              {!sidebarItemPair?.readOnly && formik.errors.calculatedConstructionFeeAmount && (
                <div className="invalid-feedback order-last">{formik.errors.calculatedConstructionFeeAmount as string}</div>
              )}
            </Col>
            <Col md={6}>
              <Label labelText={'Posebna pristojba'} />
              <PaginatedDropdown
                placeholder={!sidebarItemPair?.readOnly && 'Odaberi posebnu pristojbu'}
                onItemSelect={selected => formik.setFieldValue('specialFee', selected)}
                selected={formik.values.specialFee}
                labelKey={'value'}
                options={specialFeeOptionsLoading ? [] : specialFeeOptions}
                disabled={sidebarItemPair?.readOnly}
                error={!sidebarItemPair?.readOnly && (formik.errors.specialFee as string)}
                showErrorMessage
                clearButton
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Label labelText={'Procijenjeni troškovi građenja'} />
              <PaginatedDropdown
                placeholder={!sidebarItemPair?.readOnly && 'Odaberi procijenjene troškove građenja'}
                onItemSelect={selected => formik.setFieldValue('estimatedConstructionCost', selected)}
                selected={formik.values.estimatedConstructionCost}
                labelKey={'value'}
                options={estimatedConstructionCostLoading ? [] : estimatedConstructionCostOptions}
                disabled={sidebarItemPair?.readOnly}
                error={!sidebarItemPair?.readOnly && (formik.errors.estimatedConstructionCost as string)}
                showErrorMessage
              />
            </Col>
            <Col md={6}>
              <Label labelText={'Posebna naknada za započetu izgradnju'} />
              <div className="d-flex align-items-center border rounded">
                <NumericFormat
                  decimalScale={2}
                  allowNegative={false}
                  thousandSeparator="."
                  decimalSeparator=","
                  customInput={Input}
                  value={formik.values.specialConstructionCommencementFee ?? ''}
                  onValueChange={values => formik.setFieldValue('specialConstructionCommencementFee', values.floatValue)}
                  disabled={sidebarItemPair?.readOnly}
                  className={
                    !sidebarItemPair?.readOnly && formik.errors.specialConstructionCommencementFee
                      ? 'is-invalid'
                      : 'flex-grow-1 border-0 shadow-none'
                  }
                />
              </div>
              {!sidebarItemPair?.readOnly && formik.errors.specialConstructionCommencementFee && (
                <div className="invalid-feedback order-last">{formik.errors.specialConstructionCommencementFee as string}</div>
              )}
            </Col>
          </Row>
        </div>
        {!sidebarItemPair?.readOnly && (
          <div className={'case-save-button'}>
            <Button
              label={'Spremi'}
              size={'lg'}
              disabled={!formik.dirty || !formik.isValid}
              isLoading={savingConstructionFee}
              color={'info'}
              onClick={() => formik.handleSubmit()}
            />
          </div>
        )}
      </FormikProvider>
    </>
  );
};

export default ConstructionFee
